import { useEffect, useState } from "react";
import { Loader2, RotateCcw, Save, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { EmailPreview } from "@/components/admin/EmailPreview";

const btn = "inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-4 py-2 transition-colors disabled:opacity-60";
const FIELD_LABELS = { subject: "Temat", heading: "Nagłówek", intro: "Treść główna", docs_text: "Tekst nad linkami do dokumentów", pay_text: "Tekst przy płatności", paid_text: "Tekst gdy opłacone", upload_text: "Tekst przy skrzynce", after_upload_text: "Tekst pod przyciskiem „Wyślij podpisany dokument” (opcjonalny)", footer: "Stopka", steps: "Kroki (każdy w nowej linii)", sign_text: "Tekst o podpisie" };

export function EmailTemplatesAdmin() {
  const [data, setData] = useState(null);
  const [ov, setOv] = useState({});
  const [saved, setSaved] = useState("");
  const [lang, setLang] = useState("pl");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/admin/email-templates").then((r) => { setData(r.data); setOv(r.data.overrides); setSaved(JSON.stringify(r.data.overrides)); }).catch(() => toast.error("Nie udało się pobrać szablonów e-mail."));
  }, []);
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-600" /></div>;

  const val = (k, f, def) => ov[k]?.[f]?.[lang] ?? def[lang];
  const setVal = (k, f, def, v) => {
    const next = JSON.parse(JSON.stringify(ov));
    next[k] = next[k] || {};
    next[k][f] = next[k][f] || {};
    if (v === def[lang] || v === undefined) delete next[k][f][lang]; else next[k][f][lang] = v;
    if (!Object.keys(next[k][f]).length) delete next[k][f];
    if (!Object.keys(next[k]).length) delete next[k];
    setOv(next);
  };
  const dirty = JSON.stringify(ov) !== saved;
  const save = async () => {
    setBusy("save");
    try {
      const r = await api.put("/admin/email-templates", { overrides: ov });
      setOv(r.data.overrides);
      setSaved(JSON.stringify(r.data.overrides));
      toast.success("Szablony e-mail zapisane.");
    } catch { toast.error("Nie udało się zapisać."); } finally { setBusy(""); }
  };
  const test = async (key) => {
    setBusy(key);
    try {
      if (dirty) await api.put("/admin/email-templates", { overrides: ov }).then((r) => { setOv(r.data.overrides); setSaved(JSON.stringify(r.data.overrides)); });
      await api.post(`/admin/email-templates/${key}/test`, null, { params: { lang } });
      toast.success("Wiadomość testowa wysłana na adres biura.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Wysyłka testowa nie powiodła się.");
    } finally { setBusy(""); }
  };

  return (
    <div data-testid="email-templates-admin" className="space-y-5">
      <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 space-y-4">
        <div>
          <h2 className="font-display font-bold text-lg">Automatyczne e-maile do klientów</h2>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
            Edytuj temat i treść każdej wiadomości. Znaczniki w klamrach (np. <code className="font-mono">{"{pakiet}"}</code>) są podmieniane danymi zamówienia. Dozwolone są proste tagi HTML jak <code className="font-mono">&lt;strong&gt;</code>.
            Zmienione pola są podświetlone; „przywróć” cofa do domyślnego tekstu. Przyciski, linki i podsumowanie danych dodawane są automatycznie.
          </p>
        </div>
        <div className="flex gap-2">
          {[["pl", "Polski"], ["en", "English"]].map(([c, l]) => (
            <button key={c} type="button" data-testid={`email-lang-${c}`} onClick={() => setLang(c)} className={`${btn} border ${lang === c ? "bg-brand-navy text-white border-brand-navy dark:bg-white dark:text-slate-900" : "border-border hover:bg-muted"}`}>{l}</button>
          ))}
        </div>
      </div>
      <Accordion type="multiple" defaultValue={["order_confirmation"]} className="space-y-3">
        {data.templates.map((tpl) => {
          const changed = Object.keys(ov[tpl.key] || {}).length;
          return (
            <AccordionItem key={tpl.key} value={tpl.key} data-testid={`email-template-${tpl.key}`} className="bg-card border border-border rounded-2xl px-5">
              <AccordionTrigger className="hover:no-underline">
                <span className="font-display font-bold text-base">{tpl.label}</span>
                {changed > 0 && <span className="ml-3 text-xs text-brand-teal font-normal">{changed} zmienionych</span>}
              </AccordionTrigger>
              <AccordionContent className="pb-5">
                <div className="grid gap-6 lg:grid-cols-2">
                  <div className="space-y-3">
                    <p className="text-[11px] text-muted-foreground">Znaczniki: {tpl.placeholders.map((p) => <code key={p} className="font-mono mr-2">{p}</code>)}</p>
                    {Object.entries(tpl.fields).map(([f, def]) => {
                      const v = val(tpl.key, f, def);
                      const isChanged = ov[tpl.key]?.[f]?.[lang] !== undefined;
                      const long = f !== "subject" && f !== "heading";
                      const cls = `bg-background text-sm ${isChanged ? "border-brand-teal ring-1 ring-brand-teal/40" : ""}`;
                      return (
                        <div key={f}>
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-xs font-semibold text-muted-foreground">{FIELD_LABELS[f] || f}</label>
                            {isChanged && <button type="button" data-testid={`email-reset-${tpl.key}-${f}`} onClick={() => setVal(tpl.key, f, def, undefined)} className="text-[11px] text-brand-teal hover:underline inline-flex items-center gap-1"><RotateCcw size={10} /> przywróć</button>}
                          </div>
                          {long ? <Textarea data-testid={`email-field-${tpl.key}-${f}`} rows={3} value={v} onChange={(e) => setVal(tpl.key, f, def, e.target.value)} className={cls} />
                                : <Input data-testid={`email-field-${tpl.key}-${f}`} value={v} onChange={(e) => setVal(tpl.key, f, def, e.target.value)} className={cls} />}
                        </div>
                      );
                    })}
                    <button type="button" data-testid={`email-test-${tpl.key}`} onClick={() => test(tpl.key)} disabled={!!busy} className={`${btn} border border-border hover:bg-muted`}>
                      {busy === tpl.key ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Wyślij test na adres biura ({lang.toUpperCase()})
                    </button>
                  </div>
                  <EmailPreview templateKey={tpl.key} lang={lang} overrides={ov} />
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
      <div className="sticky bottom-4 flex justify-end">
        <div className="flex items-center gap-3 bg-card border border-border rounded-full pl-4 pr-2 py-2 shadow-lg">
          {dirty && <span data-testid="email-unsaved" className="text-xs text-amber-600 dark:text-amber-400">Niezapisane zmiany</span>}
          <button type="button" data-testid="email-save-button" onClick={save} disabled={busy === "save" || !dirty} className={`${btn} bg-brand-teal text-white hover:bg-brand-tealDark`}>
            {busy === "save" ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Zapisz szablony
          </button>
        </div>
      </div>
    </div>
  );
}
